import { request } from './base';
import { NamesSteps } from '@/helpers/auth';
import { snakify } from '@/helpers/common';

export interface CompPoints {
  chargeable?: {
    points: number;
  };
  persistent?: {
    points: number;
  };
}

export interface RegisterFormCheckboxes {
  termsAcceptanceAgeAcceptance: boolean;
  receivePromos: boolean;
  receiveSmsPromos: boolean;
  receivePromosViaPhoneCalls: boolean;
  agreedToPartnerPromotions: boolean;
  receivePromosMain?: boolean; // tech field
}

export interface RegisterForm extends RegisterFormCheckboxes {
  email: string;
  password: string;
  currency: string;
  personalIdNumber?: string;
}

interface RegisterFormApi
  extends Omit<RegisterForm, 'termsAcceptanceAgeAcceptance'> {
  termsAcceptance: boolean;
  ageAcceptance: boolean;
}

export interface PersonalInformationStepApi {
  firstName: string | null;
  lastName: string | null;
  dateOfBirth: string | null;
  mobilePhone?: string | null;
}

export interface AddressInformationStepApi {
  country: string | null;
  state?: string | null;
  address?: string | null;
  city?: string | null;
  postalCode: string | null;
}

export type FullInformationFormApi = PersonalInformationStepApi &
  AddressInformationStepApi;

export interface PersonalInformationStepForm
  extends Omit<FullInformationFormApi, 'dateOfBirth' | 'mobilePhone'> {
  dateOfBirth: {
    day: string;
    month: string;
    year: string;
  };
  mobilePhone: {
    code: string;
    number: string;
  };
}

export interface FullInformationForm {
  [NamesSteps.PERSONAL_INFORMATION]: PersonalInformationStepForm;
  [NamesSteps.ADDRESS_INFORMATION]: AddressInformationStepApi;
}

export interface LoginForm {
  email: string;
  password: string;
}

export interface ConfirmLogin {
  otp_attempt: string;
}

export type UserStatus = {
  name: string;
  id: string;
};

interface AddonsAuthUser {
  id?: number;
  authFieldsMissed?: string[];
  autologinLink?: any;
  autoregistered?: boolean;
  canIssueBonuses?: boolean;
  confirmedAt?: any;
  createdAt?: string;
  currentSignInAt?: string;
  depositBonusCode?: string;
  gender?: any;
  language?: string;
  twoFactorEnabled?: boolean;
  statuses?: UserStatus[];
  sumsubVerified?: boolean;
}

export type User = RegisterForm &
  FullInformationFormApi &
  AddonsAuthUser & { language: string };

export enum UpdatePlayerContext {
  Edition = 'edition',
}

export interface UpdatePlayer {
  context: UpdatePlayerContext;
  player: Partial<User>;
}

export interface Wallet {
  amountCents: number;
  availableToCashoutCents: number;
  currency: string;
}

export interface UserWallet extends Wallet {
  subCurrency: string;
  isCrypto: boolean;
  name?: string;
  icon?: string;
}

export const register = (user: RegisterFormApi) =>
  request<User>({
    method: 'POST',
    url: 'users',
    data: snakify({ user }),
  });

export const login = (user: LoginForm | ConfirmLogin) =>
  request<User>({
    method: 'POST',
    url: 'users/sign_in',
    data: snakify({ user }),
  });

export const logout = () =>
  request<null>({ method: 'DELETE', url: 'users/sign_out' });

export const player = () => request<User>({ url: 'player' });

export const updatePlayer = (data: UpdatePlayer) =>
  request<null>({
    method: 'PATCH',
    url: 'player',
    data: snakify(data),
  });

export const playerAccounts = () =>
  request<Wallet[]>({ url: 'player/accounts' });

export const allPlayerAccounts = () =>
  request<Wallet[]>({ url: 'player/accounts?compatibility=false' });

export const setPlayerAccounts = (currency: Wallet['currency']) =>
  request<Wallet>({
    method: 'POST',
    url: 'player/accounts',
    data: snakify({ currency }),
  });

export const deletePlayerAccount = (currency: Wallet['currency']) =>
  request<Wallet[]>({
    method: 'DELETE',
    url: `player/accounts/${currency}`,
  });

export const compPoints = () =>
  request<CompPoints>({
    url: 'player/comp_points',
  });

export interface RecoveryForm {
  email: string;
}

export interface NewPassword {
  password: string;
  passwordConfirmation: string;
}

export const initRecoveryPassword = (user: RecoveryForm) =>
  request<null>({
    method: 'POST',
    url: 'users/password',
    data: snakify({ errors: {}, user }),
  });

export const recoveryPassword = (
  user: NewPassword & { resetPasswordToken: string }
) =>
  request<null>({
    method: 'PUT',
    url: 'users/password',
    data: snakify({ errors: {}, user }),
  });

export const updateBonusSettings = (canIssue: boolean) =>
  request<null>({
    method: 'PATCH',
    url: 'player/update_bonus_settings',
    data: snakify({ canIssue }),
  });

export interface DepositBonusCode {
  depositBonusCode: string;
}

export const setBonusCode = (values: DepositBonusCode) =>
  request<null>({
    method: 'PATCH',
    url: 'player/set_bonus_code',
    data: snakify(values),
  });

export const clearBonusCode = () =>
  request<null>({
    method: 'DELETE',
    url: 'player/clear_bonus_code',
  });

export enum CouponCodeStatus {
  SuccessfullyActivated = 'successfully_activated',
  FailedToActivate = 'failed_to_activate',
}

export interface CouponCode {
  status: CouponCodeStatus;
}

export const setCouponCode = (values: { couponCode: string }) =>
  request<CouponCode>({
    method: 'POST',
    url: 'bonuses/coupon',
    data: snakify(values),
  });

export const confirmationEmail = (token: string, baseUrl?: string) =>
  request<null>({
    url: `users/confirmation?confirmation_token=${token}`,
    baseUrl,
  });

export const confirmationEmailPost = (email: string) =>
  request<null>({
    method: 'POST',
    url: `users/confirmation`,
    data: { user: { email } },
  });

export const confirmationCashoutAddress = (params: {
  paymentId: string;
  token: string;
}) =>
  request<null>({
    method: 'POST',
    url: 'payment_processing/confirm_cashout_address',
    data: snakify(params),
  });

export const unlockAccount = (token: string) =>
  request<null>({ url: `users/unlock?unlock_token=${token}` });

export interface AuthProviderOption {
  name: string;
  url: string;
}

export const authProvidersOptions = () =>
  request<AuthProviderOption[]>({
    url: `/info/auth_providers`,
  });

export interface CompointsRate {
  points: number;
  amountCents: number;
  currency: string;
}
export interface CompointsRatesMoney {
  group: string;
  rates: CompointsRate[];
  title: string;
  wager: number;
}

export const compPointsRatesMoney = () =>
  request<CompointsRatesMoney[]>({ url: 'comp_points/rates/money' });

interface ExchangeCompointsMoneyApi {
  group: string;
  points: number;
  currency: string;
}

export const exchangeCompointsMoney = (data: ExchangeCompointsMoneyApi) =>
  request<null>({
    method: 'POST',
    url: `comp_points/exchange/money`,
    data: { exchange: data },
  });

interface UpdatePlayerDetailsApi {
  email?: string;
  profileAttributes: {
    country?: string;
    currency?: string;
    receivePromos?: boolean;
    receiveSmsPromos?: boolean;
    receivePromosViaPhoneCalls?: boolean;
    agreedToPartnerPromotions?: boolean;
    termsAcceptance?: boolean;
  };
}

export const updatePlayerDetails = (data: UpdatePlayerDetailsApi) =>
  request<User>({
    method: 'POST',
    url: `auth_providers/update_details`,
    data: { user: snakify(data) },
  });
